import React, { useMemo, useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";

/**
 * Wiederverwendbares Live-Suchfeld + Hook für Tabellen.
 *
 * Features:
 *   - Mehrere Wörter = UND-Verknüpfung
 *   - Case-insensitive + Akzent-tolerant (NFD normalize)
 *   - Esc / X-Button zum Leeren
 *   - Treffer-Counter
 *
 * useFulltextFilter(items, fields)
 *   items   – Array
 *   fields  – Array von (string-keys ODER Resolver-Funktionen item → string)
 *   → { query, setQuery, filtered, total, matches }
 */
function normalize(s) {
    if (s === null || s === undefined) return "";
    return String(s)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")  // diakritische Zeichen entfernen
        .toLowerCase();
}

export function useFulltextFilter(items, fields) {
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        if (!items) return [];
        const q = query.trim();
        if (!q) return items;
        const tokens = normalize(q).split(/\s+/).filter(Boolean);
        return items.filter((item) => {
            const text = fields
                .map((f) => (typeof f === "function" ? f(item) : item?.[f]))
                .map(normalize)
                .join(" ");
            return tokens.every((tok) => text.includes(tok));
        });
    }, [items, query, fields]);

    return {
        query, setQuery,
        filtered,
        total: items?.length || 0,
        matches: filtered.length,
        active: query.trim().length > 0,
    };
}

/**
 * SearchBox: kontrolliertes Eingabefeld mit Icon + Clear-Button.
 */
export const SearchBox = ({ value, onChange, placeholder = "Schnellsuche…", testid = "searchbox", autoFocus = false, className = "" }) => {
    const inputRef = useRef(null);
    useEffect(() => {
        if (autoFocus) inputRef.current?.focus();
    }, [autoFocus]);

    return (
        <div className={`relative ${className}`}>
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
                ref={inputRef}
                type="search"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") onChange(""); }}
                placeholder={placeholder}
                className="h-8 w-full pl-8 pr-7 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
                data-testid={testid}
            />
            {value && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-slate-700"
                    data-testid={`${testid}-clear`}
                    aria-label="Leeren (Esc)"
                    title="Leeren (Esc)"
                >
                    <X size={12} />
                </button>
            )}
        </div>
    );
};

/** Treffer-Anzeige für die Liste — kleiner Hinweis-String. */
export const MatchCount = ({ matches, total, label = "Treffer", labelSingular = null }) => {
    const word = (n) => (n === 1 && labelSingular ? labelSingular : label);
    if (matches === total) return <span className="text-xs text-gray-500 tabular-nums">{total} {word(total)}</span>;
    return (
        <span className="text-xs text-slate-600 tabular-nums">
            <strong>{matches}</strong> von {total} {word(total)}
        </span>
    );
};

export default SearchBox;
